<template>
  <div class="modal fade dq-ticket-modal" id="reply_ticket_modal" tabindex="-1">
    <div
      class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable dq-ticket-dialog"
    >
      <div class="modal-content dq-modal-content">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-message-circle-share"></i>
            </div>

            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Post a Reply</h4>

              <div class="dq-subtitle">
                Reply to the requester or leave an internal note for support agents.
              </div>

              <!-- Optional context row -->
              <div v-if="ticket" class="dq-context mt-2">
                <span class="dq-pill">
                  <i class="ti ti-ticket"></i>
                  <span class="dq-pill-text">{{ ticket.code || "TIC-—" }}</span>
                </span>
                <span class="dq-pill dq-pill-soft">
                  <i class="ti ti-align-left"></i>
                  <span class="dq-pill-text">{{ ticket.subject || "Ticket" }}</span>
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submit">
          <div class="modal-body dq-modal-body">
            <!-- Tips -->
            <div class="dq-info-row">
              <div class="dq-info-pill">
                <i class="ti ti-bolt"></i>
                <span>Tip: Be specific — mention what you tried and what you need next.</span>
              </div>
              <div class="dq-info-pill" :class="is_internal ? 'dq-info-pill-warn' : ''">
                <i class="ti ti-eye-off" v-if="is_internal"></i>
                <i class="ti ti-eye" v-else></i>
                <span>
                  {{ is_internal
                    ? "Internal note: only support team can see this."
                    : "Public reply: requester will see this message." }}
                </span>
              </div>
            </div>

            <!-- Composer card -->
            <div class="dq-card">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-pencil"></i> Message
                </div>
                <div class="dq-card-sub">
                  Write your reply below. Use internal notes for agent-only discussions.
                </div>
              </div>

              <!-- Internal toggle row -->
              <div class="dq-toggle-row">
                <div class="dq-switch">
                  <input
                    id="internalNote"
                    class="dq-switch-input"
                    type="checkbox"
                    v-model="is_internal"
                    :disabled="!canUseInternalNotes"
                  />
                  <label class="dq-switch-label" for="internalNote">
                    <span class="dq-switch-thumb"></span>
                  </label>
                </div>

                <div class="dq-toggle-text">
                  <div class="dq-toggle-title">
                    Mark as internal note
                    <span
                      class="dq-chip ms-2"
                      :class="is_internal ? 'dq-chip-warn' : 'dq-chip-muted'"
                    >
                      <i class="ti ti-lock"></i>
                      <span>{{ is_internal ? "Internal" : "Public" }}</span>
                    </span>
                  </div>
                  <div class="dq-toggle-sub">
                    {{
                      canUseInternalNotes
                        ? "Internal notes are hidden from the requester."
                        : "You can post public replies only."
                    }}
                  </div>
                </div>
              </div>

              <!-- Quick templates -->
              <div class="dq-templates">
                <div class="dq-templates-title">
                  <i class="ti ti-sparkles"></i> Quick replies
                </div>
                <div class="dq-templates-wrap">
                  <button
                    type="button"
                    class="dq-template-btn"
                    @click="insertTemplate('Thanks — we’re looking into this and will update you shortly.')"
                  >
                    Acknowledge
                  </button>
                  <button
                    type="button"
                    class="dq-template-btn"
                    @click="insertTemplate('Could you share a screenshot and the exact error message you’re seeing?')"
                  >
                    Request info
                  </button>
                  <button
                    type="button"
                    class="dq-template-btn"
                    @click="insertTemplate('This has been fixed. Please refresh and try again. If it persists, let us know.')"
                  >
                    Resolved
                  </button>
                  <button
                    type="button"
                    class="dq-template-btn"
                    @click="insertTemplate('We’ve escalated this to the team. ETA: we’ll update you within 24 hours.')"
                  >
                    Escalate
                  </button>
                </div>
              </div>

              <!-- Textarea -->
              <div class="mt-3">
                <textarea
                  v-model.trim="body"
                  class="form-control dq-input dq-textarea"
                  rows="7"
                  placeholder="Type your reply..."
                ></textarea>

                <div class="dq-help mt-2 d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <span class="d-flex align-items-center">
                    <i class="ti ti-info-circle me-1"></i>
                    Keep it clear and actionable.
                  </span>
                  <span class="dq-counter" :class="body.length > 1200 ? 'dq-counter-warn' : ''">
                    {{ body.length }}/1500
                  </span>
                </div>
              </div>
            </div>

            <!-- (Optional) attachments placeholder -->
            <div class="dq-attach mt-3">
              <div class="dq-attach-icon">
                <i class="ti ti-paperclip"></i>
              </div>
              <div class="dq-attach-text">
                <div class="dq-attach-title">Attachments</div>
                <div class="dq-attach-sub">
                  Add uploads later if needed (UI placeholder for now).
                </div>
              </div>
              <button type="button" class="btn btn-light dq-btn dq-btn-muted" disabled>
                <i class="ti ti-upload me-1"></i>Upload
              </button>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
              type="button"
              :disabled="saving"
            >
              Cancel
            </button>

            <button
              class="btn btn-primary dq-btn dq-btn-primary"
              type="submit"
              :disabled="!canSubmit"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              Post Reply <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useTicketStore } from "@/stores/ticketStore";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/utils/toast";

const props = defineProps({
  ticketId: { type: [String, Number], required: true },
  // optional, for richer header context (code/subject)
  ticket: { type: Object, default: null },
});

const emit = defineEmits(["saved"]);

const store = useTicketStore();
const authStore = useAuthStore();
const body = ref("");
const is_internal = ref(false);
const canUseInternalNotes = computed(
  () =>
    authStore.can("Ticket.Edit") ||
    authStore.can("Ticket.Delete") ||
    authStore.can("Ticket.Write")
);

const saving = computed(() => store.loading);
const canSubmit = computed(() => {
  const msgOk = (body.value || "").trim().length > 0 && (body.value || "").trim().length <= 1500;
  return msgOk && !saving.value;
});

function insertTemplate(text) {
  if (!text) return;
  const current = (body.value || "").trim();
  body.value = current ? `${current}\n\n${text}` : text;
}

async function submit() {
  const msg = (body.value || "").trim();
  if (!msg) {
    toast("Please type a message", "error");
    return;
  }
  if (msg.length > 1500) {
    toast("Message is too long (max 1500 characters)", "error");
    return;
  }

  try {
    await store.addComment(props.ticketId, {
      body: msg,
      is_internal: canUseInternalNotes.value && !!is_internal.value,
    });

    body.value = "";
    is_internal.value = false;

    document.querySelector('#reply_ticket_modal [data-bs-dismiss="modal"]')?.click();
    emit("saved");
  } catch (e) {
    // store already toasts, but keep safe fallback
    toast("Failed to post reply", "error");
  }
}

// Optional: clear draft each time ticket changes
watch(
  () => props.ticketId,
  () => {
    body.value = "";
    is_internal.value = false;
  }
);

watch(canUseInternalNotes, (allowed) => {
  if (!allowed) is_internal.value = false;
});
</script>

<style scoped>
/* ====== DQ Modal Base ====== */
.dq-ticket-modal .dq-ticket-dialog {
  max-width: 860px;
}

.dq-modal-content {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 18px 50px rgba(16, 24, 40, 0.14);
}

.dq-modal-header {
  background: linear-gradient(135deg, rgba(75, 48, 136, 0.10), rgba(23, 125, 188, 0.08));
  border-bottom: 1px solid rgba(16, 24, 40, 0.08);
  padding: 18px 18px;
}

.dq-modal-body {
  padding: 16px 18px 18px;
  background: #fff;
}

.dq-modal-footer {
  border-top: 1px solid rgba(16, 24, 40, 0.08);
  background: #fff;
  padding: 14px 18px;
}

/* Header layout */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dq-hero {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 26px rgba(75, 48, 136, 0.18);
  flex-shrink: 0;
}

.dq-hero i {
  font-size: 20px;
}

.dq-hero-indigo {
  background: rgba(75, 48, 136, 0.12);
  color: #4b3088;
  border: 1px solid rgba(75, 48, 136, 0.18);
}

.dq-headtext .dq-title {
  font-weight: 800;
  letter-spacing: -0.02em;
}

.dq-subtitle {
  font-size: 12.5px;
  color: rgba(16, 24, 40, 0.65);
  margin-top: 2px;
}

.dq-context {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 6px 10px;
  background: rgba(75, 48, 136, 0.08);
  border: 1px solid rgba(75, 48, 136, 0.14);
  color: rgba(16, 24, 40, 0.85);
  max-width: 100%;
}

.dq-pill-soft {
  background: rgba(23, 125, 188, 0.06);
  border-color: rgba(23, 125, 188, 0.14);
}

.dq-pill i {
  font-size: 14px;
  opacity: 0.9;
}

.dq-pill-text {
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 520px;
}

/* Info pills */
.dq-info-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.dq-info-pill {
  flex: 1 1 320px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(23, 125, 188, 0.06);
  border: 1px solid rgba(23, 125, 188, 0.14);
  color: rgba(16, 24, 40, 0.82);
  font-size: 12.5px;
}

.dq-info-pill i {
  font-size: 16px;
  color: rgba(23, 125, 188, 0.9);
}

.dq-info-pill-warn {
  background: rgba(242, 101, 34, 0.06);
  border-color: rgba(242, 101, 34, 0.16);
}

.dq-info-pill-warn i {
  color: rgba(242, 101, 34, 0.95);
}

/* Card */
.dq-card {
  background: #fff;
  border: 1px solid rgba(16, 24, 40, 0.08);
  border-radius: 16px;
  padding: 14px;
}

.dq-card-head {
  margin-bottom: 10px;
}

.dq-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  color: rgba(16, 24, 40, 0.90);
}

.dq-card-sub {
  margin-top: 4px;
  font-size: 12.5px;
  color: rgba(16, 24, 40, 0.60);
}

/* Toggle row */
.dq-toggle-row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(16, 24, 40, 0.03);
  border: 1px dashed rgba(16, 24, 40, 0.16);
}

.dq-toggle-text {
  flex: 1;
}

.dq-toggle-title {
  font-weight: 800;
  color: rgba(16, 24, 40, 0.88);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.dq-toggle-sub {
  margin-top: 2px;
  font-size: 12.5px;
  color: rgba(16, 24, 40, 0.60);
}

/* Switch */
.dq-switch {
  width: 46px;
  height: 28px;
  position: relative;
  flex-shrink: 0;
}

.dq-switch-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.dq-switch-label {
  cursor: pointer;
  display: block;
  width: 46px;
  height: 28px;
  background: rgba(16, 24, 40, 0.18);
  border-radius: 999px;
  position: relative;
  transition: all 0.2s ease;
}

.dq-switch-thumb {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 8px 16px rgba(16, 24, 40, 0.18);
  transition: all 0.2s ease;
}

.dq-switch-input:checked + .dq-switch-label {
  background: rgba(242, 101, 34, 0.55);
}

.dq-switch-input:checked + .dq-switch-label .dq-switch-thumb {
  transform: translateX(18px);
}

/* Templates */
.dq-templates {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(16, 24, 40, 0.06);
}

.dq-templates-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 12.5px;
  color: rgba(16, 24, 40, 0.78);
  margin-bottom: 8px;
}

.dq-templates-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dq-template-btn {
  border: 1px solid rgba(16, 24, 40, 0.10);
  background: #fff;
  color: rgba(16, 24, 40, 0.78);
  padding: 7px 10px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 12px;
  transition: all 0.15s ease;
}

.dq-template-btn:hover {
  transform: translateY(-1px);
  border-color: rgba(75, 48, 136, 0.22);
  box-shadow: 0 12px 22px rgba(16, 24, 40, 0.08);
}

/* Inputs */
.dq-input {
  border-radius: 14px;
  border: 1px solid rgba(16, 24, 40, 0.12);
  padding: 12px 12px;
}

.dq-textarea {
  resize: vertical;
  min-height: 160px;
}

.dq-help {
  font-size: 12px;
  color: rgba(16, 24, 40, 0.55);
}

.dq-counter {
  font-weight: 800;
  font-size: 12px;
  color: rgba(16, 24, 40, 0.60);
}

.dq-counter-warn {
  color: rgba(242, 101, 34, 0.95);
}

/* Chips */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid rgba(16, 24, 40, 0.10);
}

.dq-chip i {
  font-size: 14px;
}

.dq-chip-muted {
  background: rgba(16, 24, 40, 0.04);
  color: rgba(16, 24, 40, 0.70);
}

.dq-chip-warn {
  background: rgba(242, 101, 34, 0.08);
  border-color: rgba(242, 101, 34, 0.16);
  color: rgba(242, 101, 34, 0.98);
}

/* Attach placeholder */
.dq-attach {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(16, 24, 40, 0.08);
  background: rgba(16, 24, 40, 0.02);
}

.dq-attach-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(23, 125, 188, 0.08);
  border: 1px solid rgba(23, 125, 188, 0.14);
  color: rgba(23, 125, 188, 0.95);
  flex-shrink: 0;
}

.dq-attach-icon i {
  font-size: 18px;
}

.dq-attach-title {
  font-weight: 900;
  color: rgba(16, 24, 40, 0.86);
}

.dq-attach-sub {
  margin-top: 2px;
  font-size: 12.5px;
  color: rgba(16, 24, 40, 0.60);
}

.dq-attach-text {
  flex: 1;
}

/* Buttons */
.dq-btn {
  border-radius: 14px;
  font-weight: 800;
  padding: 10px 12px;
}

.dq-btn-muted {
  border: 1px solid rgba(16, 24, 40, 0.10);
}

.dq-btn-primary {
  box-shadow: 0 14px 26px rgba(75, 48, 136, 0.20);
}

.dq-close {
  border-radius: 12px;
}
</style>
